/* ==========================================================================
   AURUM BREW — announcement ticker: fit the track to the screen
   assets/aurum-ticker-fit.js
   --------------------------------------------------------------------------
   WHAT WAS WRONG

   Measured on the live site at a 2844px viewport:

       .horizontal-ticker__container   scrollWidth 18504px
       devicePixelRatio                1.35
       → composited layer              ~24,975 device pixels wide

   A GPU's maximum texture dimension is commonly 16,384px. A layer past that
   cannot be rasterised as one texture, so the compositor tiles it and keeps
   re-rasterising tiles as the animation moves — which is exactly the stutter
   that shows up on a wide desktop and not on a phone, where the same track
   is a fraction of the width. The section builds the strip by repeating the
   messages enough to cover the screen, so the wider the monitor, the worse
   it gets. That is the opposite of how it should scale.

   THE SECOND BUG, WHICH IS WHY DURATION IS SET HERE TOO

   aurum-ticker.css pins the duration at 75s with !important and a comment
   saying "100px/s". That arithmetic only held for the track width the CSS
   was written against. The loop translates -50%, so actual speed is

       (scrollWidth / 2) / duration

   At 18504px that is 9252 / 75 = 123px/s, not 100. The ticker therefore runs
   noticeably faster on a wide screen than on a laptop, and faster still on an
   ultrawide — a fixed duration cannot describe a variable distance. Speed is
   the thing that should be constant, so duration is computed from the width.

   HOW THE TRIM STAYS SEAMLESS

   The loop depends on the container holding the message set exactly twice and
   sliding -50%. Items are therefore only ever removed in matched pairs — one
   from the first half and its twin from the second — so the two halves stay
   identical and the wrap is still invisible. Anything that would break that
   symmetry (an odd item count, too few items to work with) is left alone and
   only the duration is corrected.

   The original markup is cached before the first trim and restored before
   each re-fit, so widening the window rebuilds from the full strip rather
   than from an already-shortened one. Without that, dragging a window wider
   would leave a visible gap that could never be refilled.
   ========================================================================== */
(function () {
  'use strict';

  var SPEED_DESKTOP = 100; /* px per second — the intended pace */
  var SPEED_MOBILE  = 65;  /* deliberately slower; see aurum-ticker.css */

  /* How much track to keep beyond one screen. The strip must cover the
     viewport at every moment of the loop, so each half needs to be at least
     a screen wide; the margin absorbs sub-pixel rounding and a late-loading
     font reflowing the text slightly wider. */
  var COVER = 1.25;

  var container = null;
  var originalHTML = null;
  var resizeTimer = null;

  function speed() {
    return window.matchMedia('(max-width: 749px)').matches ? SPEED_MOBILE : SPEED_DESKTOP;
  }

  function applyDuration() {
    /* Half the strip is one full pass, because of the -50% translate. */
    var travel = container.scrollWidth / 2;
    if (!travel) return;
    var seconds = travel / speed();
    /* The stylesheet uses !important, so this has to as well to win. */
    container.style.setProperty('animation-duration', seconds.toFixed(2) + 's', 'important');
  }

  function fit() {
    if (!container) return;

    if (originalHTML === null) {
      originalHTML = container.innerHTML;
    } else {
      container.innerHTML = originalHTML;
    }

    var items = container.children;
    var n = items.length;

    /* Odd counts cannot be two matching halves — whatever this markup is, it
       is not the shape this trim assumes. Leave the DOM untouched and just
       fix the pace. */
    if (n < 4 || n % 2 !== 0) { applyDuration(); return; }

    var half = n / 2;
    var need = window.innerWidth * COVER;

    /* Walk the first half, accumulating width, until one screen is covered.
       k is how many items of each half we keep. */
    var acc = 0, k = 0;
    for (var i = 0; i < half; i++) {
      acc += items[i].getBoundingClientRect().width;
      k++;
      if (acc >= need) break;
    }

    /* k must be a DIVISOR of half, not just any count.

       The strip is the message list repeated over and over. Here that list is
       two messages — a 250px welcome and a 906px shipping line — so the half
       reads 250, 906, 250, 906 … Cutting at the first k that happens to cover
       the screen gave k = 7, which ends the half on a 250 and places it
       directly against the 250 that opens the next one: two identical
       messages side by side at the seam, every loop.

       Snapping k up to a divisor of half keeps a whole number of repetitions,
       so the pattern continues across the wrap no matter how many messages
       are in the bar — the period necessarily divides half. For half = 16 the
       candidates are 1, 2, 4, 8, 16; k = 7 becomes 8. */
    while (k < half && half % k !== 0) k++;

    if (k >= half) { applyDuration(); return; }   /* already minimal */

    /* Remove from the back of each half, second half first so the indices of
       the first half are still valid when we get to them. */
    var j;
    for (j = half * 2 - 1; j >= half + k; j--) container.removeChild(items[j]);
    for (j = half - 1; j >= k; j--) container.removeChild(items[j]);

    applyDuration();
  }

  function init() {
    container = document.querySelector('.horizontal-ticker__container');
    if (!container) return;
    fit();

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fit, 200);
    });

    /* Web fonts land after first paint and change the text width, which moves
       where the strip needs to end. Re-fit once they are ready. */
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(fit).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
